import type { ColumnDef } from '@tanstack/react-table'
import { Trash2 } from 'lucide-react'
import type { DifficultyInfo } from '@/components/admin/problems/types'
import { getDifficultyColorClass, getDifficultyLabel } from '@/lib/difficulty'

/**
 * Column pieces shared by the admin tables (master questions, system design).
 * Anything genuinely per-table — name, source, study plans — stays with its
 * own table.
 */

/** The difficulty pill, identical across every admin table. */
export function DifficultyBadge({
  difficulty,
}: {
  difficulty: DifficultyInfo | null | undefined
}) {
  if (!difficulty) return null
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getDifficultyColorClass(difficulty)}`}
    >
      {getDifficultyLabel(difficulty)}
    </span>
  )
}

/**
 * Order two rows by how many of their labels the active filter selected,
 * falling back to the total label count. Rows with fewer matches sort first.
 */
export function compareByFilterMatchCount(
  labelsA: string[],
  labelsB: string[],
  activeFilter: string[],
): number {
  if (activeFilter.length === 0) {
    return labelsA.length - labelsB.length
  }

  const countMatching = (labels: string[]) =>
    labels.filter((label) => activeFilter.includes(label)).length

  const matchCountA = countMatching(labelsA)
  const matchCountB = countMatching(labelsB)

  if (matchCountA !== matchCountB) {
    return matchCountA - matchCountB
  }

  return labelsA.length - labelsB.length
}

interface DeleteColumnOptions<TRow> {
  /** Name shown in the confirmation prompt. */
  getName: (row: TRow) => string
  onDelete: (id: string) => void
  /** Button tooltip, e.g. "Delete problem". */
  title: string
}

/** Trailing trash-can column with a confirm-before-delete prompt. */
export function createDeleteColumn<TRow extends { id: string }>({
  getName,
  onDelete,
  title,
}: DeleteColumnOptions<TRow>): ColumnDef<TRow> {
  return {
    id: 'delete',
    header: '',
    size: 50,
    enableSorting: false,
    cell: ({ row }) => (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          // eslint-disable-next-line no-alert -- User confirmation before destructive action
          if (window.confirm(`Delete "${getName(row.original)}"?`)) {
            onDelete(row.original.id)
          }
        }}
        className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive rounded p-1.5 transition-colors"
        title={title}
      >
        <Trash2 size={14} />
      </button>
    ),
  }
}
